<script setup lang="ts">
    import { computed, onBeforeMount, onBeforeUnmount, ref, toRefs } from "vue";
    import emitter from "tiny-emitter/instance";
    import { AxiosError } from "axios";
    import store from "@/store";
    import { flashWarning } from "@/utils";
    import AdminLockBorder from "@/components/AdminLockBorder.vue";
    import KForm from "@/components/forms/Form/KForm.vue";
    import { useKForm } from "@/composables/useKForm";
    import { NumberValidationRule } from "@/components/forms/inputs/Number/numberValidation";
    import { BESOKSFORM_OPTIONS, Besokstyp } from "@/tabs/Oppenvard/types";
    import { Trigger } from "@/components/forms/Form/types";

    const props = defineProps<{
        tabId: number;
        parameters?: {
            besokstypId: number;
        };
    }>();

    const { parameters, tabId } = toRefs(props);

    const { inputErrors, formErrors, handleErrorResponse } = useKForm();

    const formRef = ref();

    const besokstyp = computed(() =>
        store.getters["besokstyp/besokstypById"]({
            id: parameters?.value?.besokstypId,
        })
    );

    const isEditing = computed(() => !!besokstyp.value);

    const hasUnsavedData = computed(() => formRef.value?.hasUnsavedData());

    defineExpose({
        hasUnsavedData,
        tabId,
    });

    const formTriggers: Trigger[] = [
        {
            fieldName: "namn",
            dependentOn: "typkod",
            action: ({
                value,
                dependentOnValue,
                initial = false,
            }: {
                value: unknown;
                dependentOnValue: unknown;
                initial?: boolean | undefined;
            }) => {
                if (!initial) {
                    // Föreslår namn baserat på typkoden
                    const typkod = store.getters["besokstypkod/besokstypkodById"]({ id: dependentOnValue });
                    value = typkod?.displayName ?? null;
                }
                return { value };
            },
        },
    ];

    const setTabName = () => {
        store.commit("tabs/setTabName", {
            id: tabId.value,
            name: isEditing.value ? "Redigera besökstyp" : "Lägg till besökstyp",
        });
    };

    // TODO convert to useTabs in v3
    const closeTab = () => {
        if (formRef.value.hasUnsavedData()) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: "Du har osparad data. Vill du stänga utan att spara?",
                buttons: [
                    {
                        title: "Fortsätt redigera",
                        type: "secondary",
                    },
                    {
                        title: "Ja, stäng utan att spara",
                        type: "primary",
                        action: () => {
                            store.dispatch("tabs/closeTab", tabId.value);
                        },
                    },
                ],
            });

            return;
        }

        store.dispatch("tabs/closeTab", tabId.value);
    };

    const onSubmit = async (besokstyp: Besokstyp) => {
        try {
            if (isEditing.value) {
                await store.dispatch("besokstyp/updateBesokstyp", {
                    besokstyp,
                });
            } else {
                await store.dispatch("besokstyp/createBesokstyp", {
                    besokstyp,
                });
            }

            // TODO: Det borde inte behöva göras en ny hämtning manuellt, men av någon anledning fylls inte
            // nuvarande filter på med nya objekt. (Funkar nedanstående även utan page och limit?)
            await store.dispatch("besokstyp/fetchBesokstyper", {});

            closeTab();
        } catch (thrownError) {
            const error = thrownError as AxiosError;

            handleErrorResponse(error);
        }
    };

    const fetchBesokstypkoder = async ({ query }: { query?: string } = {}) => {
        try {
            const params: { q?: string; [key: string]: unknown } = {};

            if (query) {
                params.q = query;
            }

            params.is_active = true;

            const { subsetName } = await store.dispatch("besokstypkod/fetchBesokstypkoder", {
                filterParams: params,
                displayMode: true,
                limit: 0,
            });

            return store.getters["besokstypkod/besokstypkodSubset"]({ subsetName, displayMode: true });
        } catch (error) {
            flashWarning("Kunde inte hämta besökstypkoder");
        }
    };

    const fetchFormData = async () =>
        besokstyp.value ?? {
            ordning: 0,
            aktiv: true,
        };

    onBeforeMount(async () => {
        await store.dispatch("besokstyp/addSubscriber");

        setTabName();
    });

    onBeforeUnmount(async () => {
        await store.dispatch("besokstyp/removeSubscriber");
    });
</script>

<template>
    <div class="besokstyp-form">
        <KForm
            ref="formRef"
            id="besokstyp"
            :initialValuesGetter="fetchFormData"
            :submit="onSubmit"
            submitContent="Spara och stäng"
            cancelContent="Avbryt"
            :errors="inputErrors"
            :formErrors="formErrors"
            @cancel="closeTab"
            :triggers="formTriggers"
        >
            <template #header>
                <AdminLockBorder />
                <h2 v-if="isEditing">Redigera besökstyp</h2>
                <h2 v-else>Lägg till besökstyp</h2>
            </template>
            <template #default>
                <div class="form-content">
                    <KAutocomplete
                        name="typkod"
                        label="Typkod"
                        :options="fetchBesokstypkoder"
                        :onSearch="fetchBesokstypkoder"
                        validation="required"
                    />
                    <KText name="namn" label="Namn" validation="required" />
                    <KCheckbox name="gruppbesok" label="Gruppbesök" valueType="boolean" valign="center" />
                    <KCheckbox name="teambesok" label="Teambesök" valueType="boolean" valign="center" />
                    <KAutocomplete name="form" label="Besöksform" :options="BESOKSFORM_OPTIONS" validation="required" />
                    <KNumber name="ordning" label="Ordning" :validation="[[NumberValidationRule.Min, 0]]" />
                    <KCheckbox name="aktiv" label="Aktiv" valueType="boolean" valign="center" />
                </div>
            </template>
        </KForm>
    </div>
</template>

<style lang="scss" scoped></style>
